import { AdobeAnalyticsUserRole } from '@/hooks/AdobeAnalyticsHooks/AdobeAnalyticsUserRole';
import {
  ADMIN_ACCESS_GROUP,
  COMMERCIAL_ACCESS_GROUP,
  GLOBAL_ACCESS_GROUP,
  IdToken,
  USER_ACCESS_GROUPS,
  USER_ACCESS_LEVEL,
  UserAccessLevel,
  UserInfo,
} from '@/types/auth';
import jwt, { JwtPayload } from 'jwt-decode';

const FIVE_MIN_IN_MS = 1000 * 60 * 5;
export const USER_HAVE_NO_ACCESS_TO_ANY_RESOURCE = 'USER_HAVE_NO_ACCESS_TO_ANY_RESOURCE';

// Anchored so non-country groups never leak a country code: an unanchored match would read
// CESAPP_COMMERCIAL_USERS as CESAPP_CO and grant Colombia. Mirrors infrastructure/cube/conf/country-filter.js.
const COUNTRY_GROUP = /^CESAPP_([A-Z]{2})$/;

export const hasTokenExpired = (currentAccessToken: string): boolean => {
  const decodedCurrentAccessToken = jwt<JwtPayload>(currentAccessToken);
  const expirationDate = new Date(0);
  expirationDate.setUTCSeconds(decodedCurrentAccessToken.exp);
  const now = new Date();
  return expirationDate.getTime() - now.getTime() < FIVE_MIN_IN_MS;
};

// Multi-group locales arrive bracket-wrapped ("[CESAPP_TH, CESAPP_TW]"), single-group ones bare.
const parseGroups = (locale: string): string[] => {
  const groups: string[] = [];
  for (const rawGroup of locale.split(',')) {
    const group = rawGroup
      .replace(/[\[\]]/g, '')
      .trim()
      .toUpperCase();
    if (group) {
      groups.push(group);
    }
  }
  return groups;
};

const getCountriesFromGroups = (groups: string[]): string[] => {
  if (groups.includes(GLOBAL_ACCESS_GROUP) || groups.includes(ADMIN_ACCESS_GROUP)) return [];
  const seen = new Set<string>();
  const countries: string[] = [];
  for (const group of groups) {
    const country = group.match(COUNTRY_GROUP)?.[1];
    if (country && !seen.has(country)) {
      seen.add(country);
      countries.push(country);
    }
  }
  return countries;
};

// Mirrors cube's isAuthorizedLocale: only a country/global/admin group grants a data scope.
// COMMERCIAL_ACCESS_GROUP is an entitlement flag, so it alone cannot authorize a session.
const isAuthorizedGroup = (group: string): boolean =>
  group === ADMIN_ACCESS_GROUP || group === GLOBAL_ACCESS_GROUP || COUNTRY_GROUP.test(group);

export const checkUserAccess = (idToken: string) => {
  const decoded = jwt<IdToken>(idToken);
  if (!decoded.locale || !parseGroups(decoded.locale).some(isAuthorizedGroup)) {
    throw new Error(USER_HAVE_NO_ACCESS_TO_ANY_RESOURCE);
  }
};

interface GetAdobeAnalyticsUserRoleProps {
  isAdmin: boolean;
  isGlobalUser: boolean;
}

const isAdminUser = (accessLevel: UserAccessLevel): boolean => accessLevel === USER_ACCESS_LEVEL.ADMIN;

const getAdobeAnalyticsUserRole = ({ isAdmin, isGlobalUser }: GetAdobeAnalyticsUserRoleProps) => {
  if (isAdmin) return AdobeAnalyticsUserRole.DEVELOPER;
  if (isGlobalUser) return AdobeAnalyticsUserRole.GLOBAL_USER;
  return AdobeAnalyticsUserRole.LOCAL_USER;
};

const getUserAccessLevel = (groups: string[]): UserAccessLevel => {
  if (groups.includes(USER_ACCESS_GROUPS[USER_ACCESS_LEVEL.ADMIN])) {
    return USER_ACCESS_LEVEL.ADMIN;
  }
  if (groups.includes(USER_ACCESS_GROUPS[USER_ACCESS_LEVEL.GLOBAL_USER])) {
    return USER_ACCESS_LEVEL.GLOBAL_USER;
  }
  return USER_ACCESS_LEVEL.LOCAL_USER;
};

const normalizeOverrideCountries = (overrideCountries?: string[]): string[] => {
  if (!overrideCountries?.length) return [];
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const raw of overrideCountries) {
    const code = raw.trim().toUpperCase();
    if (code && !seen.has(code)) {
      seen.add(code);
      normalized.push(code);
    }
  }
  return normalized;
};

export interface AccessSimulation {
  countries?: string[];
  commercialAccess?: boolean;
}

export const getUserInfo = (idToken: string, simulation: AccessSimulation = {}): UserInfo => {
  const decoded = jwt<IdToken>(idToken);
  if (decoded.token_use !== 'id') {
    throw new Error('ID token expected');
  }

  const tokenGroups = parseGroups(decoded.locale);
  const rawAccessLevel = getUserAccessLevel(tokenGroups);

  // Only an admin (CES_ADMINS) may simulate a country
  // Derived from the raw token so an active override, which flips accessLevel/isAdmin, can't hide the control.
  const canSimulate = rawAccessLevel === USER_ACCESS_LEVEL.ADMIN;

  const simulatedCountries = canSimulate ? normalizeOverrideCountries(simulation.countries) : [];
  const isSimulating = simulatedCountries.length > 0;

  // Simulation replaces the token groups wholesale, so admin and commercial membership can only come
  // back through the dev-tools toggles rather than leaking from the real token.
  const groups = isSimulating
    ? simulatedCountries
        .map(code => `CESAPP_${code}`)
        .concat(simulation.commercialAccess ? [COMMERCIAL_ACCESS_GROUP] : [])
    : tokenGroups;

  const accessLevel = isSimulating ? USER_ACCESS_LEVEL.LOCAL_USER : rawAccessLevel;
  const isAdmin = isAdminUser(accessLevel);
  const countries = isSimulating ? simulatedCountries : getCountriesFromGroups(tokenGroups);

  return {
    firstName: decoded.given_name,
    lastName: decoded.family_name,
    email: decoded.email,
    username: decoded.identities[0]?.userId,
    countries,
    isAdmin,
    accessLevel,
    canSimulate,
    hasCommercialAccess: isAdmin || groups.includes(COMMERCIAL_ACCESS_GROUP),
    sub: decoded.sub,
    adobeAnalyticsRole: getAdobeAnalyticsUserRole({ isAdmin, isGlobalUser: countries.length === 0 }),
  };
};
